import { deprecatedLayoutRuleKeys, LayoutRules, PcbNodeTypes, } from "@buildwithflux/models";
import { Records, SortOrder } from "@buildwithflux/shared";
import { pickBy, trim } from "lodash";
import * as parsel from "parsel-js";
import { nestedNodeDelimiter, PcbNodeHelper } from "../../../nodes";
import { systemDefaultRulesSuffix } from "../../rules";
import { pcbRuleImportantString } from "../constants";
import { computeEffectiveNetForNode } from "../helpers/computeEffectiveNetForNode";
import { PcbRuleSetType, PcbRuleSetDefaultSpecificityMap, } from "../models";
import { PcbSelectorMatcher } from "../PcbSelectorMatcher";
import { PcbSystemDefaultLayoutRulesFactory } from "./PcbSystemDefaultLayoutRulesFactory";
function getPcbLayoutRuleSetsForElement(elementsMap, elementUid) {
    const element = elementsMap[elementUid];
    if (element && element.part_version_data_cache) {
        return element.part_version_data_cache?.pcbLayoutRuleSets || {};
    }
    return null;
}
/**
 * Takes elements, nodes, a subset of document data, and existing rules then
 * returns rules according to a node or a selector.
 *
 * Some static utility functions dealing with rule sets are mixed in.
 */
export class PcbLayoutRuleCompiler {
    nets;
    pcbLayoutSelectorMatching;
    elements;
    defaultRootRuleSets;
    pcbLayoutNodes;
    runtimeRulesManager;
    // NOTE: below is derived data from pcbLayoutRules
    globalRules;
    globalRulesForSublayouts;
    uidAttributeRulesIndex;
    uidAttributeRulesIndexForSublayouts;
    computeEffectiveNetForNodeCache = { allNetNodesCache: new Map() };
    // For each net uid that is pre-flattened for net inheritance, we cache the flattened rules
    netInheritanceCache = new Map();
    constructor(elements, nets, pcbLayoutRules, pcbLayoutNodes, documentData, runtimeRulesManager, logger) {
        this.nets = nets;
        this.elements = elements;
        this.pcbLayoutNodes = pcbLayoutNodes;
        this.defaultRootRuleSets = PcbSystemDefaultLayoutRulesFactory.getSystemDefaultLayoutRules(documentData.uid);
        this.pcbLayoutSelectorMatching = new PcbSelectorMatcher(elements, nets, pcbLayoutNodes, documentData, logger);
        this.runtimeRulesManager = runtimeRulesManager;
        // NOTE: performance optimization for rules that match only a single
        // node each. Most of these are system generated rules. See
        // createUidAttributeSelector. In a large 7000-node project like
        // support/induct-acu-v2-rev4, this reduces the number of rules to
        // iterate over from ~1200 to 6 for each node. On first load, this
        // results in ~30s bake instead of ~60s.
        const uidAttributeRules = pickBy(pcbLayoutRules, (ruleSet) => PcbLayoutRuleCompiler.getUidAttributeValue(ruleSet.selector));
        this.globalRules = pickBy(pcbLayoutRules, (ruleSet) => !uidAttributeRules[ruleSet.uid]);
        this.globalRulesForSublayouts = PcbLayoutRuleCompiler.filterGlobalRulesForSubLayouts(this.globalRules);
        this.uidAttributeRulesIndex = PcbLayoutRuleCompiler.constructUidAttributeRulesIndex(uidAttributeRules);
        this.uidAttributeRulesIndexForSublayouts = PcbLayoutRuleCompiler.constructUidAttributeRulesIndex(PcbLayoutRuleCompiler.filterGlobalRulesForSubLayouts(uidAttributeRules));
    }
    /**
     * @see getNodeIdsToReplace also in bakeNodesFromLatestPatch
     */
    static constructUidAttributeRulesIndex(pcbLayoutRules) {
        return Object.values(pcbLayoutRules ?? {}).reduce((acc, ruleSet) => {
            const uid = PcbLayoutRuleCompiler.getUidAttributeValue(ruleSet.selector);
            if (uid) {
                const ruleSetForUid = acc[uid] ?? {};
                ruleSetForUid[ruleSet.uid] = ruleSet;
                acc[uid] = ruleSetForUid;
            }
            return acc;
        }, {});
    }
    /**
     * Returns node IDs embedded in selectors. For example:
     *   [uid=mynodeid]
     *
     * Counterexample:
     *   footprint #mynodeid
     *
     * @see idSelectorMatchesObjectData for matching logic
     * @see attributeSelectorMatchesObjectData for matching logic
     * @see compoundSelectorMatchesObjectData for matching logic
     * @see https://parsel.verou.me/?selector=%5Buid%3D%22asdf%22%5D
     */
    static getUidAttributeValue(selector) {
        let parsedSelector;
        try {
            parsedSelector = parsel.parse(selector);
        }
        catch (_e) {
            // If it's an invalid selector, we won't be able to accurately find the uid attribute
            return;
        }
        if (parsedSelector === null) {
            return;
        }
        if (parsedSelector.type === "compound") {
            for (const listItem of parsedSelector.list) {
                if (listItem.type === "attribute" && listItem.name === "uid") {
                    return trim(listItem.value, '"');
                }
            }
        }
        if (parsedSelector.type === "attribute" && parsedSelector.name === "uid") {
            return trim(parsedSelector.value, '"');
        }
    }
    static createUidAttributeSelector(objectKey, objectUid) {
        return `${objectKey}[uid="${objectUid}"]`;
    }
    static getRuleSetBasedOnSelector(selector, pcbLayoutRuleSets) {
        return Object.values(pcbLayoutRuleSets ?? {}).find((ruleSet) => ruleSet.selector === selector);
    }
    // We are implementing this as closely as makes sense to CSS: https://developer.mozilla.org/en-US/docs/Learn/CSS/Building_blocks/Cascade_and_inheritance
    static orderRuleSetsBySpecificity(objectRules) {
        const objectRulesArray = Object.values(objectRules ?? {});
        return objectRulesArray.sort((a, b) => {
            const result = a.specificity < b.specificity ? -1 : a.specificity > b.specificity ? 1 : b.uid.localeCompare(a.uid); // fallback to alphabetical to ensure deterministic ordering
            return result * SortOrder.asc;
        });
    }
    static rankAndFlatten(ruleSets, objectSpecificRules) {
        const rankedRuleSets = PcbLayoutRuleCompiler.orderRuleSetsBySpecificity(ruleSets);
        const rankedLayoutRules = PcbLayoutRuleCompiler.flattenRules(rankedRuleSets);
        // Rules with `!important` flag, these rules will always get applied
        const importantRankedLayoutRules = Object.fromEntries(Object.entries(rankedLayoutRules).filter(([_key, rule]) => this.isRuleImportant(rule) && this.shouldApplyRule(rule)));
        // here we give objectSpecific rules full precedence over selector-based and inherited rules
        const activeObjectSpecificRules = Object.fromEntries(Object.entries(objectSpecificRules).filter(([_key, rule]) => this.shouldApplyRule(rule)));
        return { ...rankedLayoutRules, ...activeObjectSpecificRules, ...importantRankedLayoutRules };
    }
    static isRuleImportant(rule) {
        return rule && typeof rule.value === "string" && rule.value.trimEnd().endsWith(pcbRuleImportantString);
    }
    static isRuleDeprecated(rule) {
        return rule && deprecatedLayoutRuleKeys.includes(rule.key);
    }
    static getSanitizedRuleValue(value) {
        if (typeof value === "string") {
            return value.trim();
        }
        return value;
    }
    // This method will flatten all incoming rules in their order into one final list to apply to the node
    static flattenRules(rankedRuleSets) {
        const result = {};
        for (const ruleSet of rankedRuleSets) {
            for (const rule of Object.values(ruleSet.rules ?? {})) {
                if (this.shouldApplyRule(rule) && !this.isRuleImportant(result[rule.key])) {
                    result[rule.key] = { ...rule, fromSystemDefault: this.isSystemDefaultRule(ruleSet) };
                }
            }
        }
        return result;
    }
    static isSystemDefaultRule(rule) {
        // Question: Why not `rule.type === PcbRuleSetType.systemDefaultGlobal`?
        return rule.uid.includes(systemDefaultRulesSuffix);
    }
    static shouldApplyRule(rule) {
        return !rule.disabled && rule.value !== undefined && rule.value !== null;
    }
    /**
     * Filter out global rules for sub layout node. Global rules should NEVER apply to sub layout nodes,
     * except rules with `!important` keyword
     *
     * TODO: Move all `static` helper method in this class to other helper files?
     *
     * @param {PcbRuleSetsMap} pcbLayoutRules - the global rule set
     */
    static filterGlobalRulesForSubLayouts(pcbLayoutRules) {
        const resultRuleSetForSubLayout = {};
        for (const [ruleSetKey, ruleSet] of Object.entries(pcbLayoutRules)) {
            const resultRules = Object.fromEntries(Object.values(ruleSet.rules ?? {})
                .filter((rule) => this.isRuleImportant(rule))
                .map((rule) => [rule.key, rule]));
            resultRuleSetForSubLayout[ruleSetKey] = {
                ...ruleSet,
                rules: resultRules,
            };
        }
        return resultRuleSetForSubLayout;
    }
    /**
     * For a given node, this will look at the following rule sets for a specific
     * node, and then rank and flatten them.
     *
     * NOTE - How this works:
     * 1. First we grab all rule sets (see detail below) for this node
     *   - Special case: if this node is under a sublayout, filter out all rules under GlobalRuleSets,
     *     except rules with `!important` keyword
     *   - Special case: for KeepOut/KeepIn rules, we want to consider the belonging net as well
     * 2. Then, we rank rule sets with their specificity
     * 3. Then, we flatten the rule sets, resulting in a map of LayoutRules
     * 4. Finally, we make sure rules with `!important` keyword always win
     *
     *
     * NOTE:
     * runtimePcbLayoutRules - Rules set from Footprint
     * defaultRootRuleSetsWithSpecificity -  System default rule for this node type
     * inheritedRulesWithSpecificity - Rules inherited from parents’ rules. This also means that we need to calculate rules for parent first
     * selectorRulesWithSpecificity - Rules set in Rule panel (on the left sidebar), “global” rule for a node type, AND rule sets from footprint (i.e. runtimePcbLayoutRules)
     * foreignRulesWithSpecificity - Example of this rule is say an element U1 on PCB board, rules for this element U1 should be applied to all its pads
     * objectSpecificRules - Rules set via the object panel in the right sidebar - inline rules, highest priority
     *
     * HACK:
     * dontSaveCache: set to true if we want to avoid getObjectData caching, see the comment there
     */
    getFlattenedRules(pcbNode, dontSaveCache = false) {
        const ruleSets = this.getRuleSetsForNode(pcbNode.uid, dontSaveCache);
        const objectSpecificRules = this.pcbLayoutNodes[pcbNode.uid]?.pcbNodeRuleSet ?? {};
        const rankedAndFlattenedRules = PcbLayoutRuleCompiler.rankAndFlatten(ruleSets, objectSpecificRules);
        for (const ruleKey in rankedAndFlattenedRules) {
            const rule = rankedAndFlattenedRules[ruleKey];
            if (!rule) {
                continue;
            }
            // Create a new object to avoid mutating the original rule object, and bypass readonly
            rankedAndFlattenedRules[ruleKey] = {
                ...rule,
                value: PcbLayoutRuleCompiler.getSanitizedRuleValue(rule.value),
            };
        }
        return rankedAndFlattenedRules;
    }
    /* Get all rulesets for the given node, except for rules set directly on the node. */
    getRuleSetsForNode(nodeUid, dontSaveCache = false) {
        if (!nodeUid) {
            return {};
        }
        // For some rules (like keepouts) we want to inherit from the belonging net as well
        // NOTE: belonging net is not the same as the parent, nor this is normal inheritance of rules
        const pcbNode = this.pcbLayoutNodes[nodeUid];
        const parentNetUid = pcbNode && pcbNode.type !== PcbNodeTypes.net
            ? computeEffectiveNetForNode(pcbNode, this.pcbLayoutNodes, this.nets, this.computeEffectiveNetForNodeCache)
            : undefined;
        const parentNetInheritingRules = parentNetUid
            ? this.getNetInheritedRuleSetWithSpecificity(parentNetUid, [
                "keepOut",
                "maxKeepOut",
                "minKeepOut",
                "keepIn",
                "maxKeepIn",
                "minKeepIn",
                "protected",
            ])
            : {};
        // Default rules are returned with a specificity of -2, so they are overridden by inherited rules
        // and all normal selector rules.
        const defaultRootRuleSetsWithSpecificity = this.getRuleSetsForNodeWithSpecificity(this.defaultRootRuleSets, nodeUid, PcbRuleSetType.systemDefaultGlobal, dontSaveCache);
        const inheritedRulesWithSpecificity = this.getInheritedRuleSetWithSpecificity(nodeUid);
        // NOTE: runtime rules are also selector-based, so they don't have any
        // inherent priority above or below user-defined selector-based rules --
        // whether the user-defined rules or the runtime rules win depends on
        // the specificity of their selectors.
        // NOTE: this lookup relies on the nodeUid being identical to the
        // ruleset ID which is true for virtual nodes.
        const runtimeRulesForNode = this.runtimeRulesManager.runtimePcbLayoutRules[nodeUid];
        const globalRules = PcbNodeHelper.isNodeUnderSubLayout(nodeUid)
            ? { ...this.globalRulesForSublayouts, ...this.uidAttributeRulesIndexForSublayouts[nodeUid] }
            : { ...this.globalRules, ...this.uidAttributeRulesIndex[nodeUid] };
        const selectorRulesWithSpecificity = this.getRuleSetsForNodeWithSpecificity(
        // When node is under a sub layout, ignore rules in Global Rule Sets - they should never affect nodes under sub-layout
        runtimeRulesForNode ? { ...globalRules, [nodeUid]: runtimeRulesForNode } : globalRules, nodeUid, PcbRuleSetType.selectorBased, dontSaveCache);
        const [foreignRulesForNode, foreignRuleSetSourcesMap] = this.getForeignRulesForNode(nodeUid);
        const foreignRulesWithSpecificity = this.getRuleSetsForNodeWithSpecificity(foreignRulesForNode, nodeUid, PcbRuleSetType.foreignGlobal, dontSaveCache, foreignRuleSetSourcesMap);
        // The order these are spread into the resulting object is important. Precedence is implicit
        // here. Question: Is there a faster way to get rule sets for node? seems crazy to be deriving
        // getRuleSetsForNode(..) per node, repeatedly. TODO: measure performance of this, consider caching?
        return {
            ...parentNetInheritingRules,
            ...defaultRootRuleSetsWithSpecificity,
            ...inheritedRulesWithSpecificity,
            ...selectorRulesWithSpecificity,
            ...foreignRulesWithSpecificity,
        };
    }
    /**
     * Get the first ruleset matching an exact selector (like "via") that has
     * a value for the given rule key. Used for when a node to match has not yet
     * been added to the document.
     *
     * TODO: ideally, we would have a way of selecting which rules would match
     * *projected* node, because this a pretty limited first approximation.
     */
    getRuleSetForExactSelectorAndKey(selector, ruleKey) {
        const defaultRules = Object.values(this.defaultRootRuleSets ?? {}).filter((r) => r.selector === selector);
        const selectorRules = Object.values(this.globalRules ?? {}).filter((r) => r.selector === selector && !r.disabled);
        // Ensure that the `ruleKey` rule is defined and has a meaningful value, so the user can't crash
        // their project by clearing the field.
        const ruleset = selectorRules.concat(defaultRules).find((ruleSet) => {
            const falsyValues = [null, undefined, ""];
            const rule = ruleSet.rules[ruleKey];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return rule && !falsyValues.includes(rule.value);
        });
        return ruleset?.rules;
    }
    getRuleSetsForNodeWithSpecificity(ruleSets, nodeUid, type, 
    // HACK: set to true if we want to avoid getObjectData caching, see the comment there
    dontSaveCache = false, 
    // Used by getForeignRulesForNode to pass the source map of rule sets
    ruleSetSourceMap) {
        const results = {};
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(ruleSets ?? {}).forEach((ruleSet) => {
            if (ruleSet.disabled)
                return;
            let specificity = this.pcbLayoutSelectorMatching.match(ruleSet.selector, nodeUid, ruleSetSourceMap?.[ruleSet.uid], dontSaveCache);
            if (specificity !== undefined && PcbLayoutRuleCompiler.isSystemDefaultRule(ruleSet)) {
                // System defaults have lowest priority so they can be easily overwritten by any other rule.
                const ruleSetType = "type" in ruleSet ? ruleSet.type : PcbRuleSetType.systemDefaultGlobal;
                specificity =
                    PcbRuleSetDefaultSpecificityMap[ruleSetType] ??
                        PcbRuleSetDefaultSpecificityMap[PcbRuleSetType.systemDefaultGlobal];
            }
            if (specificity !== undefined) {
                const ruleSetCopy = { ...ruleSet };
                // HACK: keepout rules need to be scoped to a specific UID which is defined as the impedancePair
                // value. This is a temporary solution so that we can support impedance keepouts as a system rule
                // rather than needing to add an object rule that the user cannot override.
                if (ruleSet.selector === "net[impedancePair]") {
                    // Substitute rule attributes with actual values
                    const nodeData = this.pcbLayoutNodes[nodeUid];
                    ruleSetCopy.rules = Object.entries(ruleSetCopy.rules).reduce((acc, [key, rule]) => {
                        acc[key] = {
                            ...rule,
                            uid: `${rule.uid}_${nodeData.impedancePair}`,
                            value: rule.value?.replace("{{impedancePair}}", nodeData.impedancePair ?? ""),
                        };
                        return acc;
                    }, {});
                }
                results[ruleSetCopy.uid] = {
                    ...ruleSetCopy,
                    specificity,
                    type: "type" in ruleSetCopy ? ruleSetCopy.type : type,
                };
            }
        });
        return results;
    }
    /**
     * For a nested element (for example, project A using element B that contains element C),
     * the node uid for that elementC will be <elementBUid_elementCUid>. Only the `pcbLayoutRuleSets`
     * under the nearest element should be counted, unless !important is used.
     *
     * Take the following setup as an example, imagine we have
     *  ├── Test Project (the top level project)
     *    ├── element A (with sub-layout)
     *      ├── node A3
     *      ├── element A1
     *         ├── element A1a
     *           ├── node A1a_node
     *      ├── element A2
     *         ├── node A2b
     *    ├── element B (with sub-layout)
     *      ├── node B1
     *      ├── node B2
     *
     * Now if we want to get foreign rules (i.e. global rule sets) for a node, we need to grab it
     * from the __nearest parent element__. For example, if we want to get the rule sets for
     * - `node B1`, then we need to grab the global rule sets under `element B`
     * - `node A3`, then we need to grab the global rule sets under `element A`
     * - `node A2b`, then we need to grab the global rule sets under `element A2`,
     *   and ignore the one under `element A`. However, if we dont have any global rule sets
     *   under `element A2`, it will fallback to the one under `element A`
     * - `node A1a_node`, then we need to grab the global rule sets under `element A1a`,
     *   and ignore the one under `element A` and `element A1`. However, if we dont have any
     *   global rule sets under `element A2`, it will fallback to the one under `element A1` first,
     *   then fallback to the one under `element A`
     *
     * The following block of function is to traverse the nested part_version_data_cache (if any), and
     * grab the nearest global rule sets for the node we are baking.
     */
    getForeignRulesForNode(nodeUid) {
        const foreignRuleSets = {};
        const foreignRuleSetSourcesMap = {};
        const returnValue = [foreignRuleSets, foreignRuleSetSourcesMap];
        if (!PcbNodeHelper.isNodeUnderSubLayout(nodeUid)) {
            return returnValue;
        }
        const [firstUid, ...restOfUids] = nodeUid.split(nestedNodeDelimiter);
        if (!firstUid) {
            // Should never happen
            return returnValue;
        }
        const topLevelElement = this.elements[firstUid];
        if (!topLevelElement) {
            // Should never happen
            return returnValue;
        }
        // Top-level rules
        const pcbRuleSetsFromTopLevelElement = getPcbLayoutRuleSetsForElement(this.elements, firstUid);
        if (pcbRuleSetsFromTopLevelElement) {
            for (const ruleSet of Object.values(pcbRuleSetsFromTopLevelElement)) {
                foreignRuleSetSourcesMap[ruleSet.uid] = firstUid;
            }
            Object.assign(foreignRuleSets, pcbRuleSetsFromTopLevelElement);
        }
        // Lower nested rules
        let nextAncestor = topLevelElement;
        let nestedUid = firstUid;
        for (const nextUid of restOfUids) {
            nestedUid = [nestedUid, nextUid].join(nestedNodeDelimiter);
            const extractedElements = nextAncestor.part_version_data_cache?.extracted_element_cache;
            if (!extractedElements) {
                // Should never happen
                break;
            }
            const newPcbLayoutRuleSets = getPcbLayoutRuleSetsForElement(extractedElements, nextUid);
            // If we found the corresponding element and there IS a global rulesets under it,
            // grab this rule set because when calculating the final rule sets for a node, we want
            // to consider all global rules defined in all parts that are ancestors of the current node
            // NOTE: The final order we apply this rule set is determined by `getRuleSetsForNodeWithSpecificity`
            if (newPcbLayoutRuleSets && !Records.isEmpty(newPcbLayoutRuleSets)) {
                for (const ruleSet of Object.values(newPcbLayoutRuleSets)) {
                    foreignRuleSetSourcesMap[ruleSet.uid] = nestedUid;
                }
                Object.assign(foreignRuleSets, newPcbLayoutRuleSets);
            }
            // go down one level
            const nextAncestorDown = extractedElements[nextUid];
            if (!nextAncestorDown) {
                // Should never happen
                break;
            }
            nextAncestor = nextAncestorDown;
        }
        return returnValue;
    }
    buildInheritedRuleset(originalRules, ruleSetIdBuilder, filter) {
        let rules = {};
        if (originalRules) {
            rules = Object.fromEntries(Object.entries(originalRules).filter(filter));
        }
        else {
            // Else it's the root node.
            rules = {};
        }
        return ruleSetIdBuilder(rules);
    }
    getInheritedRuleSetWithSpecificity(nodeUid) {
        // Inherit previously-calculated parent rules with specificity of 0 so they override defaults but
        // don't override selector rules.
        const node = this.pcbLayoutNodes[nodeUid];
        const parentNode = node?.parentUid ? this.pcbLayoutNodes[node.parentUid] : undefined;
        return this.buildInheritedRuleset(
        // TODO: [NodeType] - Fix as cast here
        parentNode?.flattenedRules, (rules) => ({
            "inherited-rules-uid": {
                specificity: PcbRuleSetDefaultSpecificityMap[PcbRuleSetType.inherited],
                uid: "inherited-rules-uid",
                selector: "parents",
                rules,
                type: PcbRuleSetType.inherited,
            },
        }), ([key, _rule]) => key in LayoutRules && LayoutRules[key].allChildNodesWillInheritThisRule);
    }
    /**
     * HACK: This function is used to compute the flattened rules for a net node, since they are used for net inheritance.
     * The result of this function is cached per net uid, since we can reuse it for multiple nodes.
     * The other normal inheritance function can treat the parent node as baked, but here we can't, as net inheritance works also
     * outside the normal tree. It would work for a trace/via but not for a pad, which might be baked before the belonging net.
     *
     * NOTE: This function will NOT be able to consider the rules that are inherited into the net node from its parent.
     * This is fine since we are only doing keepouts so far.
     */
    getNetNodeFlattenedRules(netNodeUid) {
        const cachedValue = this.netInheritanceCache.get(netNodeUid);
        if (cachedValue)
            return cachedValue;
        const netNode = this.pcbLayoutNodes[netNodeUid];
        if (!netNode)
            return undefined;
        // HACK: we want to avoid caching the result of getObjectData in the selector matcher
        // It can happen that we are calling this before a net has been properly baked, taining the
        // cache with incomplete data without impedance matching/auto trace width info
        const newValue = this.getFlattenedRules(netNode, true);
        this.netInheritanceCache.set(netNodeUid, newValue);
        return newValue;
    }
    getNetInheritedRuleSetWithSpecificity(netNodeUid, supportedRules) {
        const rules = this.getNetNodeFlattenedRules(netNodeUid);
        // Net inherited rules have specificity of -1, so they override default rules but not local and rulesets
        return this.buildInheritedRuleset(rules, (rules) => ({
            "net-inherited-rules-uid": {
                specificity: PcbRuleSetDefaultSpecificityMap[PcbRuleSetType.netInherited],
                uid: "net-inherited-rules-uid",
                selector: "net membership",
                rules,
                type: PcbRuleSetType.netInherited,
            },
        }), 
        // IMPORTANT: we exclude system defaults, as we don't want net system defaults to be more important than the ones from the nodes
        ([key, rule]) => supportedRules.includes(key) && key in LayoutRules && !rule.fromSystemDefault);
    }
}
//# sourceMappingURL=PcbLayoutRuleCompiler.js.map